import React from 'react';
import { Select, DatePicker, Checkbox, Typography } from 'antd';
import moment from 'moment';
import styles from '../../Layout/AnalyticsLayout.module.scss';
import { IBestDataset, IPlotVariable } from '../../../../../../types/analytics';
import { DateFormat } from '../../../../../../utils/StaticVariable';

const { Text } = Typography;

interface ScatterPlotInputFormProps {
  categoryDataset: IBestDataset[];
  datasetX: string | null;
  onDatasetXChange: (datasetId: string) => void;
  categories: string[];
  variablesX: IPlotVariable[];
  variableX: IPlotVariable | null;
  onVariableChange: (variableIndex: number, axis: 'X' | 'Y') => void;
  startDate: moment.Moment;
  endDate: moment.Moment;
  onStartDateChange: (date: moment.Moment | null) => void;
  onEndDateChange: (date: moment.Moment | null) => void;
  disabledStartDate: (date: moment.Moment) => boolean;
  disabledEndDate: (date: moment.Moment) => boolean;
  isForAllAnalytics: boolean;
  onCheckboxChange: () => void;
  isLoading?: boolean;
  hasError?: boolean;
}

export const ScatterPlotInputForm = ({
  categoryDataset,
  datasetX,
  onDatasetXChange,
  categories,
  variablesX,
  variableX,
  onVariableChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  disabledStartDate,
  disabledEndDate,
  isForAllAnalytics,
  onCheckboxChange,
  isLoading = false,
  hasError = false,
}: ScatterPlotInputFormProps) => {
  return (
    <div className={styles.formRow}>
      <div className={`${styles.formItem} ${styles.datasetItem}`}>
        <Text>Dataset X</Text>
        <Select
          id='Analytics-ScatterPlot-Dataset_Input'
          value={datasetX}
          onChange={onDatasetXChange}
          placeholder='Select dataset'
          disabled={isLoading}
        >
          {categories.map((category: string) => (
            <Select.OptGroup key={category} label={category}>
              {categoryDataset
                .filter((item) => item.label === category)
                .map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.value}
                  </Select.Option>
                ))}
            </Select.OptGroup>
          ))}
        </Select>
      </div>

      <div className={`${styles.formItem} ${styles.variableItem}`}>
        <Text>Variable X</Text>
        <Select
          id='Analytics-ScatterPlot-Variable_Input'
          placeholder='Select variable'
          onChange={(active) => onVariableChange(active, 'X')}
          value={variableX?.Index ? variableX.Index : 0}
          disabled={isLoading || hasError}
        >
          {variablesX
            .filter((item) => item.Name !== 'Time')
            .map((item) => (
              <Select.Option key={item.Index} value={item.Index}>
                {item.Name}
              </Select.Option>
            ))}
        </Select>
      </div>

      <div className={`${styles.formItem} ${styles.dateItem}`}>
        <Text>Start Date</Text>
        <DatePicker
          id='Analytics-ScatterPlot-StartDate_Input'
          value={moment(startDate)}
          disabledDate={disabledStartDate}
          onChange={onStartDateChange}
          format={DateFormat}
          disabled={isLoading || hasError}
        />
      </div>

      <div className={`${styles.formItem} ${styles.dateItem}`}>
        <Text>End Date</Text>
        <DatePicker
          id='Analytics-ScatterPlot-EndDate_Input'
          value={moment(endDate)}
          disabledDate={disabledEndDate}
          onChange={onEndDateChange}
          format={DateFormat}
          disabled={isLoading || hasError}
        />
      </div>

      <div className={`${styles.formItem} ${styles.checkboxItem}`}>
        <Checkbox
          id='Analytics-ScatterPlot_Checkbox'
          disabled={hasError || isLoading}
          checked={isForAllAnalytics}
          onChange={onCheckboxChange}
        />
        <Text onClick={onCheckboxChange} style={{ cursor: 'pointer' }}>
          Use this period for all analytics
        </Text>
      </div>
    </div>
  );
};

export type { ScatterPlotInputFormProps };
